import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, LogOut, ClipboardList, CalendarRange, BookUser, Clock, Download, Inbox, ListChecks, Hourglass } from "lucide-react";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import InstallHelperDialog from "@/components/InstallHelperDialog";

export default function AppShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [installOpen, setInstallOpen] = useState(false);

    const onLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate("/login");
    };

    const isManager = user?.role === "manager";

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text-primary)]">
            <header className="sticky top-0 z-20 backdrop-blur bg-[var(--bg)]/85 border-b border-[var(--border-soft)]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <NavLink to={isManager ? "/manager" : "/operative"} className="flex items-center gap-2.5">
                        <img
                            src="/scs-icon-512.png"
                            alt="SCS"
                            className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl object-cover shrink-0"
                        />
                        <div className="leading-tight min-w-0">
                            <div className="font-display text-base sm:text-lg font-semibold tracking-tight truncate" data-testid="brand-heading">
                                {user?.workspace_slug && user.workspace_slug !== "scs"
                                    ? user.workspace_name
                                    : "SCS"}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--terracotta)] font-semibold">
                                {user?.workspace_slug && user.workspace_slug !== "scs"
                                    ? "Powered by SCS"
                                    : "Simple · Clear · Systems"}
                            </div>
                        </div>
                    </NavLink>

                    <nav className="hidden xl:flex items-center gap-1">
                        <NavItem to={isManager ? "/manager" : "/operative"} icon={<LayoutDashboard size={16} />}>
                            {isManager ? "Dashboard" : "My jobs"}
                        </NavItem>
                        <NavItem to="/schedule" icon={<CalendarRange size={16} />}>Schedule</NavItem>
                        <NavItem to="/work-in-progress" icon={<Hourglass size={16} />}>In progress</NavItem>
                        {isManager && <NavItem to="/pipeline" icon={<Inbox size={16} />}>Pipeline</NavItem>}
                        {isManager && <NavItem to="/clients" icon={<BookUser size={16} />}>Clients</NavItem>}
                        {isManager && <NavItem to="/team" icon={<Users size={16} />}>Team</NavItem>}
                        {isManager && <NavItem to="/timesheets" icon={<Clock size={16} />}>Timesheets</NavItem>}
                        {isManager && <NavItem to="/settings/checklist-library" icon={<ListChecks size={16} />}>Checklists</NavItem>}
                    </nav>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <NotificationBell />
                        <div className="hidden sm:flex xl:hidden 2xl:flex flex-col items-end leading-tight">
                            <span className="text-sm font-medium" data-testid="current-user-name">{user?.name}</span>
                            <span className="pill status-scheduled capitalize" data-testid="current-user-role">
                                {user?.role}
                            </span>
                        </div>
                        <div
                            className="h-9 w-9 rounded-full flex items-center justify-center font-semibold text-sm border-2"
                            style={{
                                background: user?.color || "var(--pine-light)",
                                color: user?.color ? "#fff" : "var(--pine)",
                                borderColor: user?.color || "transparent",
                            }}
                        >
                            {initialsFromName(user?.name)}
                        </div>
                        <button
                            data-testid="install-app-btn"
                            onClick={() => setInstallOpen(true)}
                            className="btn-ghost !p-2 hidden sm:inline-flex"
                            aria-label="Install SCS on this device"
                            title="Install SCS on this device">
                            <Download size={16} />
                        </button>
                        <button data-testid="logout-btn" onClick={onLogout} className="btn-ghost !p-2" aria-label="Sign out">
                            <LogOut size={16} />
                        </button>
                    </div>
                </div>

                <nav className="xl:hidden flex items-center gap-2 px-4 pb-3 overflow-x-auto">
                    <NavItem to={isManager ? "/manager" : "/operative"} icon={<LayoutDashboard size={14} />} compact>
                        {isManager ? "Dashboard" : "My jobs"}
                    </NavItem>
                    <NavItem to="/schedule" icon={<CalendarRange size={14} />} compact>Schedule</NavItem>
                    <NavItem to="/work-in-progress" icon={<Hourglass size={14} />} compact>In progress</NavItem>
                    {isManager && <NavItem to="/pipeline" icon={<Inbox size={14} />} compact>Pipeline</NavItem>}
                    {isManager && <NavItem to="/clients" icon={<BookUser size={14} />} compact>Clients</NavItem>}
                    {isManager && <NavItem to="/team" icon={<Users size={14} />} compact>Team</NavItem>}
                    {isManager && <NavItem to="/timesheets" icon={<Clock size={14} />} compact>Timesheets</NavItem>}
                    {isManager && <NavItem to="/settings/checklist-library" icon={<ListChecks size={14} />} compact>Checklists</NavItem>}
                    <button
                        data-testid="install-app-btn-mobile"
                        onClick={() => setInstallOpen(true)}
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)] whitespace-nowrap">
                        <Download size={14} /> Install
                    </button>
                </nav>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                <Outlet />
            </main>

            <InstallHelperDialog open={installOpen} onClose={() => setInstallOpen(false)} />
        </div>
    );
}

function NavItem({ to, icon, children, compact }) {
    return (
        <NavLink
            to={to}
            end
            className={({ isActive }) =>
                `inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 ${compact ? "py-1.5 text-xs" : "py-2 text-sm"} font-medium transition-colors ${
                    isActive
                        ? "bg-[var(--pine)] text-white"
                        : "text-[var(--text-secondary)] hover:text-[var(--pine)] hover:bg-[var(--pine-light)]"
                }`
            }
        >
            {icon}{children}
        </NavLink>
    );
}


function initialsFromName(name) {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
